import * as React from 'react';
import clsx from 'clsx';
import { Button, Icon, Spinner, Typography } from '@/components/common';
import { TooltipComponent } from '@/components/common/tooltip';
import Icons from '@/const/icons';
import { useForm } from 'react-hook-form';
import { InputCheck, InputText, Textarea } from '../form';
import useModal from '@/hooks/useModal';
import { ModalHeader } from './ModalHeader';

export interface FlagReasonOption {
	id?: number;
	label: string;
	count?: number;
	isCustom?: boolean;
	description?: string;
}

interface SavedFlagReason {
	id?: number;
	label: string;
	description?: string;
}

interface FlagReasonsPickerProps {
	options: FlagReasonOption[];
	selected: string[];
	onChange: (selected: string[]) => void;
	onCreateReason?: (reason: SavedFlagReason) => void;
	onEditReason?: (
		previousLabel: string,
		reason: SavedFlagReason
	) => void | Promise<void>;
	onDeleteReason?: (option: FlagReasonOption) => void | Promise<void>;
	onRefreshReasons?: () => unknown;
	onViewChange?: (view: 'list' | 'create') => void;
	onDeleteModalOpenChange?: (isOpen: boolean) => void;
	disabled?: boolean;
	loading?: boolean;
	hideSelectedChips?: boolean;
}

/** Chip that represents one selected flagging reason, matching the style of
 * FilterChip used in the Flag column filter. */
const ReasonChip: React.FC<{ label: string; onRemove: () => void }> = ({
	label,
	onRemove,
}) => (
	<span className="inline-flex items-center gap-2 bg-[#8039DF1A] border border-[#8039DF33] rounded-lg px-3 py-1.5 text-sm text-primary max-w-[200px]">
		<span className="truncate">{label}</span>
		<button
			type="button"
			onClick={onRemove}
			className="shrink-0 hover:opacity-70 transition-opacity"
			aria-label={`Remove ${label}`}
		>
			<Icon src={Icons.close} width={16} height={16} className="text-primary" />
		</button>
	</span>
);

const ReasonOption: React.FC<{
	option: FlagReasonOption;
	isChecked: boolean;
	disabled?: boolean;
	register: ReturnType<typeof useForm>['register'];
	onToggle: () => void;
	onEdit?: () => void;
	onDelete?: () => void;
}> = ({
	option,
	isChecked,
	disabled,
	register,
	onToggle,
	onEdit,
	onDelete,
}) => (
	<InputCheck
		name={`flag-reason-${option.label}`}
		register={register}
		checked={isChecked}
		disabled={disabled}
		className="group w-full px-6 py-3 hover:bg-gray-5"
		classNameLabel="flex-1 min-w-0"
		onChangeCustom={onToggle}
	>
		<span className="flex items-center justify-between gap-2 flex-1">
			<span className="flex items-center gap-2 min-w-0">
				<span className="text-base text-black truncate">{option.label}</span>
				{option.description && (
					<TooltipComponent
						classNameLabel="!font-bold !text-left"
						className="!max-w-[223px]"
						trigger={
							<span>
								<Icon
									src={Icons.infoCircle}
									className="!size-4 text-opacity-black"
								/>
							</span>
						}
						label={option.description}
					/>
				)}
				{typeof option.count === 'number' && (
					<span className="inline-flex items-center justify-center px-2 h-4 rounded-full bg-bg-primary text-primary text-[10px]">
						{String(option.count).padStart(2, '0')}
					</span>
				)}
			</span>
			{(onEdit || onDelete) && (
				<span className="hidden group-hover:flex items-center gap-2 shrink-0">
					{onEdit && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onEdit();
							}}
							aria-label={`Edit ${option.label}`}
						>
							<Icon src={Icons.editTwo} className="!size-4 text-primary" />
						</button>
					)}
					{onDelete && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onDelete();
							}}
							aria-label={`Delete ${option.label}`}
						>
							<Icon src={Icons.trash} className="!size-4 text-main-red" />
						</button>
					)}
				</span>
			)}
		</span>
	</InputCheck>
);

export const FlagReasonsPicker: React.FC<FlagReasonsPickerProps> = ({
	options,
	selected,
	onChange,
	onCreateReason,
	onEditReason,
	onDeleteReason,
	onRefreshReasons,
	onViewChange,
	onDeleteModalOpenChange,
	disabled,
	loading,
	hideSelectedChips,
}) => {
	const { register, watch, setValue } = useForm();
	const [view, setViewState] = React.useState<'list' | 'create'>('list');
	const [editingReason, setEditingReason] =
		React.useState<FlagReasonOption | null>(null);
	const [reasonToDelete, setReasonToDelete] =
		React.useState<FlagReasonOption | null>(null);
	const [isDeleting, setIsDeleting] = React.useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
	const {
		Modal: ModalDeleteReason,
		show: showDeleteModal,
		hide: hideDeleteModal,
	} = useModal();

	const setView = (next: 'list' | 'create') => {
		setViewState(next);
		onViewChange?.(next);
	};

	const searchTerm: string = watch('flag-reason-search') || '';
	const newReasonLabel: string = watch('new-reason-label') || '';
	const newReasonDescription: string = watch('new-reason-description') || '';

	const filteredOptions = React.useMemo(() => {
		if (!searchTerm) return options;
		return options.filter((option) =>
			option.label.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [searchTerm, options]);

	const customOptions = filteredOptions.filter((option) => option.isCustom);
	const defaultOptions = filteredOptions.filter((option) => !option.isCustom);

	const toggleReason = (label: string) => {
		if (disabled) return;
		onChange(
			selected.includes(label)
				? selected.filter((item) => item !== label)
				: [...selected, label]
		);
	};

	const openCreateView = () => {
		setEditingReason(null);
		setValue('new-reason-label', '');
		setValue('new-reason-description', '');
		setView('create');
	};

	const openEditView = (option: FlagReasonOption) => {
		setEditingReason(option);
		setValue('new-reason-label', option.label);
		setValue('new-reason-description', option.description || '');
		setView('create');
	};

	const handleSubmitReason = async () => {
		const label = newReasonLabel.trim();
		if (!label) return;
		const description = newReasonDescription.trim() || undefined;

		if (editingReason) {
			await onEditReason?.(editingReason.label, {
				id: editingReason.id,
				label,
				description,
			});
		} else {
			onCreateReason?.({ label, description });
		}

		setEditingReason(null);
		setValue('new-reason-label', '');
		setValue('new-reason-description', '');
		setView('list');
	};

	const confirmDelete = (option: FlagReasonOption) => {
		setReasonToDelete(option);
		setIsDeleteModalOpen(true);
		onDeleteModalOpenChange?.(true);
		showDeleteModal();
	};

	const closeDeleteModal = async () => {
		hideDeleteModal();
		setIsDeleteModalOpen(false);
		onDeleteModalOpenChange?.(false);
		setReasonToDelete(null);
		await onRefreshReasons?.();
	};

	const handleDelete = async () => {
		if (!reasonToDelete || isDeleting) return;
		try {
			setIsDeleting(true);
			await onDeleteReason?.(reasonToDelete);
			await closeDeleteModal();
		} finally {
			setIsDeleting(false);
		}
	};

	if (view === 'create') {
		return (
			<div className="flex flex-col md:min-h-[500px]">
				<p className="text-base text-black text-left mb-2">
					{editingReason ? 'Edit custom reason' : 'Add custom reason'}
				</p>
				<div className="relative w-full flex-1">
					<InputText
						name="new-reason-label"
						title="Reason Label"
						markAsRequired
						register={register}
						placeholder="e.g., Duplicate account detected"
						className="w-full"
						classNameInput="!mt-2"
						disabled={disabled}
						onChange={({ target }) =>
							setValue('new-reason-label', target.value)
						}
					/>
					<Typography
						type="regular-12"
						className="text-opacity-black mt-1 text-left mb-6"
					>
						Short and descriptive name shown in the list.
					</Typography>
					<Textarea
						name="new-reason-description"
						title="Description (Optional)"
						register={register}
						rows={4}
						placeholder="Provide additional context or instructions for reviewers..."
						className="w-full"
						disabled={disabled}
						onChangeCustom={({ target }) =>
							setValue(
								'new-reason-description',
								(target as HTMLTextAreaElement).value
							)
						}
					/>
				</div>
				<div className="flex items-center gap-4 mt-6">
					<Button
						label="Back"
						decoration="secondary"
						type="button"
						size="full"
						className="!h-12"
						onClick={() => {
							setEditingReason(null);
							setView('list');
						}}
					/>
					<Button
						label={editingReason ? 'Save Changes' : 'Add Reason'}
						decoration="primary"
						type="button"
						size="full"
						icon={editingReason ? undefined : Icons.plus}
						classNameButton="!size-4"
						iconLeft={!editingReason}
						disabled={disabled || !newReasonLabel.trim()}
						className="!h-12"
						onClick={handleSubmitReason}
					/>
				</div>
			</div>
		);
	}

	return (
		<div>
			{!isDeleteModalOpen && (
				<>
					<p className="text-base text-black text-left mb-2">Flagging reason</p>

					<div className="relative w-full rounded-3xl bg-white border border-opacity-primary py-4">
						<div className="px-4">
							<InputText
								name="flag-reason-search"
								register={register}
								placeholder="Search"
								iconLeft={Icons.search}
								className="w-full"
								classNameIcon="!top-[28%] !w-4 !h-4"
								classNameInput="pl-9 border-none !bg-gray-15 h-11 !mt-0"
								disabled={disabled}
								onChange={({ target }) =>
									setValue('flag-reason-search', target.value)
								}
							/>

							{!hideSelectedChips && selected.length > 0 && (
								<div className="flex flex-wrap items-center gap-2 mt-3 max-h-[80px] overflow-y-auto scroll-custom">
									{selected.map((label) => (
										<ReasonChip
											key={label}
											label={label}
											onRemove={() => toggleReason(label)}
										/>
									))}
								</div>
							)}
						</div>

						{loading ? (
							<div className="w-full flex justify-center py-6">
								<Spinner type="loadingPage" />
							</div>
						) : (
							<>
								{customOptions.length > 0 && (
									<div>
										<Typography
											type="regular-14"
											className="text-primary mt-4 mb-2 px-6 block text-left border-b pb-2"
										>
											Custom
										</Typography>
										<div className="overflow-auto scroll-custom max-h-[130px]">
											{customOptions.map((option) => (
												<ReasonOption
													key={option.label}
													option={option}
													isChecked={selected.includes(option.label)}
													disabled={disabled}
													register={register}
													onToggle={() => toggleReason(option.label)}
													onEdit={
														onEditReason
															? () => openEditView(option)
															: undefined
													}
													onDelete={
														onDeleteReason
															? () => confirmDelete(option)
															: undefined
													}
												/>
											))}
										</div>
									</div>
								)}

								{defaultOptions.length > 0 && (
									<div>
										<Typography
											type="regular-14"
											className="text-primary mt-4 mb-2 px-6 block text-left border-b pb-2"
										>
											Default
										</Typography>
										<div className="overflow-auto scroll-custom max-h-[130px]">
											{defaultOptions.map((option) => (
												<ReasonOption
													key={option.label}
													option={option}
													isChecked={selected.includes(option.label)}
													disabled={disabled}
													register={register}
													onToggle={() => toggleReason(option.label)}
												/>
											))}
										</div>
									</div>
								)}
							</>
						)}

						{onCreateReason && (
							<div className="border-t border-[#0D06161A] mt-2 pt-3 px-4">
								<button
									type="button"
									disabled={disabled}
									onClick={openCreateView}
									className={clsx(
										'flex items-center gap-2 text-primary font-semibold text-xs',
										disabled && 'opacity-40 cursor-not-allowed'
									)}
								>
									<Icon src={Icons.addCircle} className="!size-4" />
									Add custom reason
								</button>
							</div>
						)}
					</div>
				</>
			)}

			<ModalDeleteReason
				title={
					<ModalHeader title="Delete Unique Flag" icon={Icons.flagFilled} />
				}
				classNameCardContainer="!w-[532px] !h-[248px] !py-8 !px-10"
				classNameContainerChildren="!overflow-visible"
				backgroundColor="white"
				withXButton
				onClose={closeDeleteModal}
			>
				<Typography type="regular-16" className="text-center">
					<strong>{reasonToDelete?.count ?? 0} clients</strong> have this flag
					right now; if you take it off, it&apos;ll be removed from all users
					with that flag.
				</Typography>
				<div className="flex items-center justify-center gap-x-4 mt-6">
					<Button
						label="Cancel"
						decoration="secondary"
						type="button"
						size="fit"
						className="!h-12"
						onClick={closeDeleteModal}
					/>
					<Button
						label="Delete Flag"
						decoration="danger-outline"
						type="button"
						size="fit"
						className="!h-12"
						disabled={isDeleting}
						onClick={handleDelete}
					/>
				</div>
			</ModalDeleteReason>
		</div>
	);
};
